// aifab - CreateRunModal v4.0
// Purpose-first run creation: Purpose → Model → Data → Pipeline+Options
// run_purpose is user-declared upfront; all subsequent steps adapt to it.

import { useState, useEffect, useCallback } from 'react';
import { apiFetch }            from '../hooks/apiFetch';
import { useApi }              from '../hooks/useApi';
import { Modal, Err, Spinner } from '../components/common';

// ── Purpose definitions ───────────────────────────────────────────────────────

const PURPOSES = [
  {
    code:        'CLASSIFICATION',
    label:       'Classification',
    icon:        '🎯',
    description: 'Predict a category or label',
    detail:      'Supervised learning - outputs a class label. Use for triage, routing, intent detection.',
    families:    ['bert', 'classical', 'embedding'],
    configPurpose: 'CLASSIFICATION',
  },
  {
    code:        'DOMAIN_PRETRAIN',
    label:       'Domain Pre-training',
    icon:        '🧠',
    description: 'Adapt a model to your domain',
    detail:      'Runs Masked Language Modelling on your corpus. Output becomes a reusable base model.',
    families:    ['bert'],
    configPurpose: 'CORPUS',
  },
  {
    code:        'REGRESSION',
    label:       'Regression',
    icon:        '📈',
    description: 'Predict a numeric value',
    detail:      'Outputs a continuous number. Use for scores, durations, costs, severity ratings.',
    families:    ['classical'],
    configPurpose: 'REGRESSION',
  },
  {
    code:        'NER',
    label:       'Named Entity Recognition',
    icon:        '🏷️',
    description: 'Extract named entities from text',
    detail:      'Token-level classification using a custom TRAIN plugin. Requires IOB-formatted dataset.',
    families:    ['bert'],
    configPurpose: 'NER',
    requiresPlugin: true,
  },
  {
    code:        'CUSTOM',
    label:       'Custom',
    icon:        '⚙️',
    description: 'Bring your own training script',
    detail:      'The platform manages execution. You supply the TRAIN plugin. Any model family.',
    families:    ['bert', 'classical', 'llm', 'embedding'],
    configPurpose: 'CUSTOM',
    requiresPlugin: true,
  },
];

const PURPOSE_MAP = Object.fromEntries(PURPOSES.map(p => [p.code, p]));

// Family group → display order within model selector
const FAMILY_GROUP_LABELS = {
  bert:      'BERT / Transformer',
  classical: 'Classical ML',
  llm:       'Large Language Model',
  embedding: 'Embedding Model',
};

// ── Step progress ─────────────────────────────────────────────────────────────

function StepBar({ step, steps, onJump }) {
  return (
    <div style={{ display:'flex', alignItems:'center', marginBottom:24, gap:0 }}>
      {steps.map((label, i) => {
        const idx    = i + 1;
        const done   = step > idx;
        const active = step === idx;
        return (
          <div key={i} style={{ display:'flex', alignItems:'center', flex: i < steps.length-1 ? 1 : 'none' }}>
            <div
              onClick={() => done && onJump(idx)}
              style={{
                display:'flex', flexDirection:'column', alignItems:'center', gap:4,
                cursor: done ? 'pointer' : 'default',
              }}
            >
              <div style={{
                width:26, height:26, borderRadius:'50%', display:'flex',
                alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700,
                background: (done || active) ? 'var(--brand-orange)' : 'var(--bg-elevated)',
                color: (done || active) ? '#fff' : 'var(--text-muted)',
                border: `2px solid ${(done || active) ? 'var(--brand-orange)' : 'var(--border)'}`,
                transition: 'all 0.2s',
              }}>
                {done ? '✓' : idx}
              </div>
              <span style={{
                fontSize:10, fontWeight: active ? 600 : 400,
                color: active ? 'var(--brand-orange)' : done ? 'var(--text-secondary)' : 'var(--text-muted)',
                whiteSpace:'nowrap',
              }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex:1, height:2, margin:'0 6px', marginBottom:14,
                background: done ? 'var(--brand-orange)' : 'var(--border)',
                transition: 'background 0.3s',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Purpose card ──────────────────────────────────────────────────────────────

function PurposeCard({ purpose, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '14px 16px',
        borderRadius: 'var(--radius-md)',
        border: `2px solid ${selected ? 'var(--brand-orange)' : 'var(--border)'}`,
        background: selected ? 'rgba(232,74,0,0.04)' : 'var(--bg-surface)',
        cursor: 'pointer',
        transition: 'all 0.15s',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <div style={{ fontSize:22, lineHeight:1, marginTop:2 }}>{purpose.icon}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{
          fontSize:13, fontWeight:600,
          color: selected ? 'var(--brand-orange)' : 'var(--text-primary)',
          marginBottom:3,
        }}>
          {purpose.label}
          {purpose.requiresPlugin && (
            <span style={{
              marginLeft:6, fontSize:10, fontWeight:500,
              padding:'1px 6px', borderRadius:4,
              background:'rgba(37,99,235,0.08)', color:'var(--info)',
            }}>Custom plugin</span>
          )}
        </div>
        <div style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.5 }}>
          {purpose.description}
        </div>
        {selected && (
          <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5, marginTop:6 }}>
            {purpose.detail}
          </div>
        )}
      </div>
      {selected && (
        <div style={{
          width:18, height:18, borderRadius:'50%', flexShrink:0,
          background:'var(--brand-orange)', color:'#0F172A',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:10, fontWeight:700,
        }}>✓</div>
      )}
    </div>
  );
}

// ── Model version card ────────────────────────────────────────────────────────

function ModelCard({ model, version, selected, onClick }) {
  const isDapt = model.source_type === 'DAPT_ARTIFACT';
  const perf   = model.usecase_performance;
  return (
    <div
      onClick={onClick}
      style={{
        padding: '12px 14px',
        borderRadius: 'var(--radius-md)',
        border: `2px solid ${selected ? 'var(--brand-orange)' : 'var(--border)'}`,
        background: selected ? 'rgba(232,74,0,0.04)' : 'var(--bg-surface)',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div style={{
            fontSize:13, fontWeight:600,
            color: selected ? 'var(--brand-orange)' : 'var(--text-primary)',
          }}>
            {model.display_name}
            {isDapt && (
              <span style={{
                marginLeft:6, fontSize:10, fontWeight:500, padding:'1px 6px',
                borderRadius:4, background:'rgba(22,163,74,0.08)', color:'var(--success)',
              }}>Domain-adapted</span>
            )}
          </div>
          <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
            {isDapt
              ? `Run #${model.dapt_run_number} · ${model.family_code}`
              : model.base_model_name
            }
          </div>
        </div>
        {version && (
          <div style={{ fontSize:11, color:'var(--text-secondary)', textAlign:'right' }}>
            v{version.version_number}
          </div>
        )}
      </div>
      {perf && (
        <div style={{
          marginTop:8, padding:'6px 10px', borderRadius:4,
          background:'rgba(245,158,11,.07)', border:'1px solid rgba(245,158,11,.2)',
          display:'flex', alignItems:'center', gap:8,
        }}>
          <span style={{ fontSize:10, color:'#f59e0b', fontWeight:700 }}>★ Champion</span>
          <span style={{ fontSize:10, color:'#334155' }}>
            {perf.metric_key}: {typeof perf.metric_value === 'number'
              ? perf.metric_key === 'F1' || perf.metric_key === 'Accuracy'
                ? `${(perf.metric_value*100).toFixed(1)}%`
                : perf.metric_value.toFixed(4)
              : '—'}
          </span>
          <span style={{ fontSize:9, color:'#64748B' }}>Run #{perf.run_number}</span>
        </div>
      )}
    </div>
  );
}

const PURPOSE_LABELS = {
  CLASSIFICATION: 'Classification', REGRESSION: 'Regression',
  NER: 'NER', DOMAIN_PRETRAIN: 'Domain Pre-train', CUSTOM: 'Custom',
};
const PURPOSE_COLORS = {
  CLASSIFICATION: '#3b82f6', REGRESSION: '#8b5cf6',
  NER: '#ec4899', DOMAIN_PRETRAIN: '#22c55e', CUSTOM: '#6b7280',
};


// ── Usecase selector with suggestion search + inline create ──────────────────
function UsecaseSelector({
  projectId, usecaseId, usecaseObj, ucQuery, ucResults, ucLoading,
  showNewUc, newUcName, newUcPurpose, newUcDesc, creatingUc,
  setUcQuery, setUcResults, setUcLoading, setShowNewUc,
  setNewUcName, setNewUcPurpose, setNewUcDesc, setCreatingUc, onSelect,
}) {
  // Search on query change (debounced)
  useEffect(() => {
    if (!projectId) return;
    setUcLoading(true);
    const t = setTimeout(() => {
      const qs = ucQuery ? `&q=${encodeURIComponent(ucQuery)}` : '';
      apiFetch(`/usecases?project_id=${projectId}${qs}`)
        .then(d => setUcResults(d || []))
        .catch(() => setUcResults([]))
        .finally(() => setUcLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [projectId, ucQuery]);

  const createUsecase = async () => {
    if (!newUcName.trim()) return;
    setCreatingUc(true);
    try {
      const uc = await apiFetch('/usecases', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId, usecase_name: newUcName.trim(),
          run_purpose: newUcPurpose, description: newUcDesc || null,
        }),
      });
      onSelect({ ...uc, run_count: 0 });
      setShowNewUc(false);
      setNewUcName(''); setNewUcDesc('');
    } catch (e) { alert(e.message); }
    finally { setCreatingUc(false); }
  };

  return (
    <div>
      <p style={{ fontSize:12, color:'#334155', marginBottom:14 }}>
        Select the usecase this run belongs to, or create a new one.
        Usecases group related experiments by the problem they solve.
      </p>

      {/* Selected usecase display */}
      {usecaseObj && (
        <div style={{ marginBottom:12, padding:'10px 12px', borderRadius:6,
          background:'rgba(232,71,10,.05)', border:'1px solid var(--or)',
          display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ width:8, height:8, borderRadius:'50%', flexShrink:0,
            background: PURPOSE_COLORS[usecaseObj.run_purpose] || '#6b7280' }} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600 }}>{usecaseObj.usecase_name}</div>
            <div style={{ fontSize:10, color:'#64748B', marginTop:1 }}>
              {PURPOSE_LABELS[usecaseObj.run_purpose]} · {usecaseObj.run_count} run{usecaseObj.run_count !== 1 ? 's' : ''}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ fontSize:10 }}
            onClick={() => onSelect(null) || true}>Change</button>
        </div>
      )}

      {!usecaseObj && (
        <>
          {/* Search box */}
          <div style={{ position:'relative', marginBottom:8 }}>
            <input
              className="form-inp"
              placeholder="Search usecases or type to filter…"
              value={ucQuery}
              onChange={e => setUcQuery(e.target.value)}
              style={{ paddingRight:28 }}
            />
            {ucLoading && (
              <div style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)' }}>
                <Spinner size={12}/>
              </div>
            )}
          </div>

          {/* Suggestions */}
          {ucResults !== null && (
            <div style={{ maxHeight:220, overflowY:'auto', marginBottom:8,
              border:'1px solid var(--bd)', borderRadius:6, background:'#E4EAF4' }}>
              {ucResults.length === 0 ? (
                <div style={{ padding:'12px 14px', fontSize:11, color:'#64748B', textAlign:'center' }}>
                  {ucQuery ? `No usecases matching "${ucQuery}"` : 'No usecases in this project yet'}
                </div>
              ) : ucResults.map(uc => (
                <div key={uc.id}
                  onClick={() => onSelect(uc)}
                  style={{ padding:'9px 14px', cursor:'pointer', display:'flex',
                    alignItems:'center', gap:8, borderBottom:'1px solid var(--bd)',
                    transition:'background .1s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(232,71,10,.04)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}
                >
                  <span style={{ width:7, height:7, borderRadius:'50%', flexShrink:0,
                    background: PURPOSE_COLORS[uc.run_purpose] || '#6b7280' }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:500 }}>{uc.usecase_name}</div>
                    <div style={{ fontSize:10, color:'#64748B' }}>
                      {PURPOSE_LABELS[uc.run_purpose]} · {uc.run_count} run{uc.run_count !== 1 ? 's' : ''}
                      {uc.champion_run_id && ' · ★ has champion'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create new toggle */}
          {!showNewUc ? (
            <button className="btn btn-ghost btn-sm" style={{ width:'100%', justifyContent:'center' }}
              onClick={() => { setShowNewUc(true); setNewUcName(ucQuery); }}>
              + Create new usecase{ucQuery ? ` "${ucQuery}"` : ''}
            </button>
          ) : (
            <div style={{ padding:'12px 14px', border:'1px solid var(--bd)', borderRadius:6,
              background:'#F1F5FB', marginTop:4 }}>
              <div style={{ fontSize:11, fontWeight:600, marginBottom:10 }}>New Usecase</div>
              <div className="form-grp">
                <label className="form-lbl">Name</label>
                <input className="form-inp" value={newUcName}
                  onChange={e => setNewUcName(e.target.value)}
                  placeholder="e.g. Customer Churn" />
              </div>
              <div className="form-grp">
                <label className="form-lbl">Purpose</label>
                <select className="form-inp" value={newUcPurpose}
                  onChange={e => setNewUcPurpose(e.target.value)}>
                  {Object.entries(PURPOSE_LABELS).map(([k,v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="form-grp">
                <label className="form-lbl">Description (optional)</label>
                <textarea className="form-inp" value={newUcDesc} rows={2}
                  onChange={e => setNewUcDesc(e.target.value)}
                  placeholder="What problem does this solve?" />
              </div>
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowNewUc(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" onClick={createUsecase}
                  disabled={creatingUc || !newUcName.trim()}>
                  {creatingUc ? <Spinner size={11}/> : 'Create & Select'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export function CreateRunPage({ projectId, taskStatuses, onBack, onSaved }) {
  const [step,    setStep]    = useState(1);
  const [err,     setErr]     = useState(null);
  const [saving,  setSaving]  = useState(false);

  // Step 1 - Usecase (replaces manual run_purpose selection)
  const [usecaseId,   setUsecaseId]   = useState('');
  const [usecaseObj,  setUsecaseObj]  = useState(null);  // full usecase object
  const [ucQuery,     setUcQuery]     = useState('');
  const [ucResults,   setUcResults]   = useState(null);  // null = not loaded
  const [ucLoading,   setUcLoading]   = useState(false);
  const [showNewUc,   setShowNewUc]   = useState(false);
  const [newUcName,   setNewUcName]   = useState('');
  const [newUcPurpose,setNewUcPurpose]= useState('CLASSIFICATION');
  const [newUcDesc,   setNewUcDesc]   = useState('');
  const [creatingUc,  setCreatingUc]  = useState(false);
  // run_purpose is derived from the selected usecase
  const runPurpose = usecaseObj?.run_purpose || '';

  // Step 2 - Model
  const [models,        setModels]        = useState([]);
  const [daptArtifacts, setDaptArtifacts] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [selectedModel,     setSelectedModel]     = useState(null);

  // Step 3 - Data
  const [datasources,    setDatasources]    = useState([]);
  const [dsLoading,      setDsLoading]      = useState(false);
  const [featureConfigs, setFeatureConfigs] = useState([]);
  const [datasourceId,   setDatasourceId]   = useState('');
  const [featureConfigId, setFeatureConfigId] = useState('');

  // Step 3.5 - Script (CUSTOM purpose only)
  const [scripts,        setScripts]        = useState([]);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [customScriptId, setCustomScriptId] = useState('');

  // Param overrides (applied on top of master version params)
  const [paramValues,   setParamValues]  = useState({});  // {key: override_value}
  const [masterParams,  setMasterParams] = useState({});  // master values for diff display
  const [paramsLoading, setParamsLoading]= useState(false);

  // Feature config fork state
  const [showFork,     setShowFork]    = useState(false);
  const [forkReason,   setForkReason]  = useState('');
  const [forking,      setForking]     = useState(false);
  const [forkedConfig, setForkedConfig]= useState(null);  // created fork object

  // Step 4 - Pipeline + Options
  const [pipelineMode, setPipelineMode] = useState('none');
  const [templates,    setTemplates]    = useState([]);
  const [plugins,      setPlugins]      = useState([]);
  const [templateId,   setTemplateId]   = useState('');
  const [explicitPlugins, setExplicitPlugins] = useState({});
  const [runLabel,      setRunLabel]     = useState('');
  const [priority,      setPriority]     = useState('NORMAL');

  const purposeDef = PURPOSE_MAP[runPurpose] || null;

  // ── Load models when purpose is selected ──────────────────────────────────

  // Load scripts when CUSTOM purpose selected
  useEffect(() => {
    if (runPurpose !== 'CUSTOM') return;
    setScriptsLoading(true);
    apiFetch('/custom-scripts').then(d => {
      setScripts(d || []);
      setScriptsLoading(false);
    }).catch(() => setScriptsLoading(false));
  }, [runPurpose]);

  // Load params when a version is selected
  useEffect(() => {
    const vid = selectedVersionId;
    if (!vid || vid.startsWith('dapt:')) { setMasterParams({}); setParamValues({}); return; }
    setParamsLoading(true);
    apiFetch(`/versions/${vid}/params`)
      .then(d => {
        // endpoint returns {param_key: param_value} dict
        const master = typeof d === 'object' && !Array.isArray(d) ? d : {};
        setMasterParams(master);
        setParamValues({});  // reset overrides when model changes
      })
      .catch(() => { setMasterParams({}); setParamValues({}); })
      .finally(() => setParamsLoading(false));
  }, [selectedVersionId, projectId, selectedModel?.config_id]);

  useEffect(() => {
    if (!runPurpose || !projectId) return;
    setModelsLoading(true);
    setModels([]); setDaptArtifacts([]);
    setSelectedVersionId(''); setSelectedModel(null);

    Promise.all([
      apiFetch(`/models/selectable?project_id=${projectId}&purpose=${runPurpose}${usecaseId ? `&usecase_id=${usecaseId}` : ``}`),
      runPurpose !== 'DOMAIN_PRETRAIN'
        ? apiFetch(`/models/dapt-artifacts?project_id=${projectId}`).catch(() => [])
        : Promise.resolve([]),
    ]).then(([versionData, daptData]) => {
      // versionData is a flat list of version-level objects, each with version_id.
      // No expansion or nesting logic needed - the endpoint returns exactly what's needed.
      setModels(versionData || []);
      setDaptArtifacts(daptData || []);
    }).catch(() => {
      setModels([]); setDaptArtifacts([]);
    }).finally(() => setModelsLoading(false));
  }, [runPurpose, projectId]);

  // ── Load datasources ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!projectId) return;
    setDsLoading(true);
    apiFetch(`/datasources?project_id=${projectId}`)
      .then(d => setDatasources((d?.items ?? d ?? []).filter(ds => ds.status === 'PROFILED')))
      .catch(() => setDatasources([]))
      .finally(() => setDsLoading(false));
  }, [projectId]);

  // ── Load feature configs when datasource + purpose selected ──────────────

  useEffect(() => {
    if (!datasourceId || !purposeDef) { setFeatureConfigs([]); setFeatureConfigId(''); return; }
    const configPurpose = purposeDef.configPurpose;
    apiFetch(`/datasources/${datasourceId}/feature-configs?purpose=${configPurpose}`)
      .then(cfgs => {
        const reviewed = (cfgs || []).filter(fc => fc.user_reviewed);
        setFeatureConfigs(reviewed);
        setFeatureConfigId('');
      })
      .catch(() => { setFeatureConfigs([]); setFeatureConfigId(''); });
  }, [datasourceId, purposeDef]);

  // ── Load pipeline templates + plugins ────────────────────────────────────

  useEffect(() => {
    if (!projectId) return;
    apiFetch(`/plugins/templates?project_id=${projectId}`).then(d => setTemplates(d || [])).catch(() => {});
    apiFetch(`/plugins?project_id=${projectId}`).then(d => setPlugins(d || [])).catch(() => {});
  }, [projectId]);

  // ── Navigation guards ─────────────────────────────────────────────────────

  const canGoStep2 = !!usecaseId && !!usecaseObj;
  const canGoStep3 = !!selectedVersionId;
  const canGoStep4 = !!datasourceId && !!featureConfigId;
  const canGoScript = !!customScriptId;

  // ── Save ──────────────────────────────────────────────────────────────────

  const save = async () => {
    setErr(null);
    if (!runPurpose)       { setErr('Select a run purpose'); return; }
    if (!selectedVersionId){ setErr('Select a model'); return; }
    if (!datasourceId)     { setErr('Select a datasource'); return; }
    if (!featureConfigId)  { setErr('Select a feature config'); return; }

    // Distinguish DAPT artifact selection from standard model version
    const isDaptSelection  = selectedVersionId.startsWith('dapt:');
    const registryId   = isDaptSelection ? selectedVersionId.replace('dapt:', '') : null;
    const isConfigOnly = !isDaptSelection && selectedModel && !selectedModel.version_id;
    const versionId    = (!isDaptSelection && !isConfigOnly) ? selectedVersionId : null;
    const configId     = isConfigOnly ? selectedVersionId : null;

    const body = {
      project_id:        projectId,
      version_id:        versionId,
      dapt_registry_id:  registryId,
      model_config_id:   configId,
      datasource_id:     datasourceId,
      feature_config_id: featureConfigId,
      usecase_id:        usecaseId,
      custom_script_id:  isCustom ? customScriptId || undefined : undefined,
      run_label:         runLabel || undefined,
      priority,
      param_overrides:   Object.keys(paramValues).length > 0 ? paramValues : undefined,
      pipeline_template_id: pipelineMode === 'template' ? templateId || undefined : undefined,
      explicit_plugins:     pipelineMode === 'explicit' && Object.keys(explicitPlugins).length
                              ? explicitPlugins : undefined,
    };

    setSaving(true);
    try {
      await apiFetch('/runs', { method:'POST', body: JSON.stringify(body) });
      onSaved();
    } catch(e) {
      setErr(e.message || 'Failed to create run');
      setSaving(false);
    }
  };

  // ── Grouped models for step 2 ─────────────────────────────────────────────

  const modelsByGroup = models.reduce((acc, m) => {
    const group = m.family_group || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(m);
    return acc;
  }, {});

  const pluginsByType = plugins.reduce((acc, p) => {
    if (!acc[p.plugin_type]) acc[p.plugin_type] = [];
    acc[p.plugin_type].push(p);
    return acc;
  }, {});

  const isCustom = runPurpose === 'CUSTOM';
  const STEPS = isCustom
    ? ['Purpose', 'Model', 'Data', 'Script', 'Pipeline']
    : ['Purpose', 'Model', 'Data', 'Pipeline'];

  // Resolve display values for the summary panel from existing state -
  // no new fetches, purely derived from what step 2-4 already loaded.
  const selectedDs = datasources.find(d => d.id === datasourceId) || null;
  const selectedFc = featureConfigs.find(f => f.id === featureConfigId) || null;
  const selectedTemplate = templates.find(t => t.id === templateId) || null;
  const selectedScript = scripts.find(s => s.script_id === customScriptId) || null;
  const explicitCount = Object.values(explicitPlugins).filter(Boolean).length;

  return (
    <div className="page" style={{ maxWidth:1180, margin:'0 auto', padding:'24px 20px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}
          style={{ fontSize:12, display:'flex', alignItems:'center', gap:4 }}>
          ← Runs
        </button>
        <div style={{ fontWeight:700, fontSize:16 }}>New Run</div>
        {usecaseObj && (
          <span style={{ fontSize:11, color:'#64748B', marginLeft:4 }}>
            {usecaseObj.usecase_name}
          </span>
        )}
      </div>

      <div style={{ display:'flex', gap:32, alignItems:'flex-start' }}>
        {/* ── Wizard column ────────────────────────────────────────────── */}
        <div style={{ flex:'0 0 680px', minWidth:0 }} data-tour="createrun-steps">
      <StepBar step={step} steps={STEPS} onJump={setStep} />

      {/* ── Step 1: Usecase ─────────────────────────────────────────────── */}
      {step === 1 && (
        <>
          <UsecaseSelector
            projectId={projectId}
            usecaseId={usecaseId}
            usecaseObj={usecaseObj}
            ucQuery={ucQuery}
            ucResults={ucResults}
            ucLoading={ucLoading}
            showNewUc={showNewUc}
            newUcName={newUcName}
            newUcPurpose={newUcPurpose}
            newUcDesc={newUcDesc}
            creatingUc={creatingUc}
            setUcQuery={setUcQuery}
            setUcResults={setUcResults}
            setUcLoading={setUcLoading}
            setShowNewUc={setShowNewUc}
            setNewUcName={setNewUcName}
            setNewUcPurpose={setNewUcPurpose}
            setNewUcDesc={setNewUcDesc}
            setCreatingUc={setCreatingUc}
            onSelect={(uc) => {
              setUsecaseId(uc.id);
              setUsecaseObj(uc);
              setSelectedVersionId('');
              setSelectedModel(null);
            }}
          />
          {err && <Err msg={err} />}
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:20 }}>
            <button
              className="btn btn-primary btn-sm"
              disabled={!canGoStep2}
              onClick={() => { setErr(null); setStep(2); }}
            >
              Next →
            </button>
          </div>
        </>
      )}

      {/* ── Step 2: Model ───────────────────────────────────────────────── */}
      {step === 2 && (
        <>
          <div style={{
            display:'flex', alignItems:'center', gap:8, marginBottom:16,
            padding:'8px 12px', borderRadius:'var(--radius-sm)',
            background:'rgba(232,74,0,0.04)', border:'1px solid rgba(232,74,0,0.15)',
          }}>
            <span style={{ fontSize:16 }}>{purposeDef?.icon}</span>
            <span style={{ fontSize:12, fontWeight:600, color:'var(--brand-orange)' }}>
              {purposeDef?.label}
            </span>
            <span style={{ fontSize:11, color:'var(--text-muted)', marginLeft:'auto' }}>
              {purposeDef?.description}
            </span>
          </div>

          {modelsLoading ? <Spinner /> : (
            <>
              {/* DAPT artifacts shown first for Classification/NER */}
              {daptArtifacts.length > 0 && (
                <div style={{ marginBottom:16 }} data-tour="createrun-dapt">
                  <div style={{
                    fontSize:10, fontWeight:700, textTransform:'uppercase',
                    letterSpacing:'0.08em', color:'var(--success)', marginBottom:2,
                  }}>
                    Domain-adapted checkpoints
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:8 }}>
                    Base weights already adapted to your data via Domain Pre-training.
                    Pick one to build a new model on top of it.
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {daptArtifacts.map(a => (
                      <ModelCard
                        key={a.registry_id}
                        model={{ ...a, display_name: `${a.dapt_run_label || 'DAPT Run #' + a.dapt_run_number}`, source_type:'DAPT_ARTIFACT' }}
                        selected={selectedVersionId === `dapt:${a.registry_id}`}
                        onClick={() => {
                          setSelectedVersionId(`dapt:${a.registry_id}`);
                          setSelectedModel(a);
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ height:1, background:'var(--border)', margin:'14px 0' }} />
                </div>
              )}

              {/* Existing model configs, grouped by family. Distinct from the
                  DAPT section above: these are configs you already created -
                  selecting one starts a new run/version of that same config. */}
              {Object.keys(modelsByGroup).length > 0 && (
                <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:10 }}
                  data-tour="createrun-configs">
                  Or use one of your existing model configs:
                </div>
              )}
              {Object.entries(modelsByGroup).map(([group, groupModels]) => (
                <div key={group} style={{ marginBottom:16 }}>
                  <div style={{
                    fontSize:10, fontWeight:700, textTransform:'uppercase',
                    letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:8,
                  }}>
                    {FAMILY_GROUP_LABELS[group] || group}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {groupModels.map(m => (
                      <ModelCard
                        key={m.config_id}
                        model={m}
                        version={m}
                        selected={selectedVersionId === (m.version_id || m.config_id)}
                        onClick={() => {
                          setSelectedVersionId(m.version_id || m.config_id);
                          setSelectedModel(m);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {models.length === 0 && daptArtifacts.length === 0 && (
                <div style={{
                  textAlign:'center', padding:'24px 16px',
                  color:'var(--text-muted)', fontSize:12,
                }}>
                  No models configured for {purposeDef?.label}.
                  <br/>
                  <span style={{ fontSize:11 }}>
                    Go to <strong>Models</strong> to add a model to this project first.
                  </span>
                </div>
              )}
            </>
          )}

          {/* ── Param overrides panel (shown when model selected) ── */}
          {selectedVersionId && !selectedVersionId.startsWith('dapt:') && (
            <div style={{ marginTop:16, padding:'12px 14px',
              border:'1px solid var(--border)', borderRadius:6, background:'var(--surface-2)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <span style={{ fontSize:10, fontWeight:700, textTransform:'uppercase',
                  letterSpacing:'.1em', color:'var(--text-muted)' }}>Hyperparameters</span>
                <span style={{ fontSize:9, color:'var(--text-muted)', opacity:.6 }}>
                  Override master values for this run only
                </span>
              </div>
              {paramsLoading ? <Spinner size={14}/> : (
                Object.keys(masterParams).length === 0 ? (
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>No parameters defined</div>
                ) : (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 12px' }}>
                    {Object.entries(masterParams).map(([key, master]) => {
                      const override = paramValues[key];
                      const changed  = override !== undefined && override !== master;
                      return (
                        <div key={key} style={{ display:'flex', flexDirection:'column', gap:2 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <label style={{ fontSize:9, color:'var(--text-muted)',
                              fontFamily:'var(--font-mono)' }}>{key}</label>
                            {changed && (
                              <span style={{ fontSize:8, color:'var(--brand-orange)' }}>↑ changed</span>
                            )}
                          </div>
                          <input
                            style={{
                              fontSize:11, padding:'4px 8px',
                              background: changed ? 'rgba(232,71,10,.07)' : 'var(--surface-3)',
                              border: `1px solid ${changed ? 'var(--brand-orange)' : 'var(--border)'}`,
                              borderRadius:4, color:'var(--text-primary)',
                              fontFamily:'var(--font-mono)',
                            }}
                            value={override !== undefined ? override : master}
                            onChange={e => {
                              const val = e.target.value;
                              setParamValues(prev => {
                                const next = {...prev};
                                if (val === master) delete next[key]; // no override
                                else next[key] = val;
                                return next;
                              });
                            }}
                          />
                          {changed && (
                            <span style={{ fontSize:8, color:'var(--text-muted)',
                              fontFamily:'var(--font-mono)' }}>master: {master}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          )}

          {err && <Err msg={err} />}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:20 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)}>← Back</button>
            <button
              className="btn btn-primary btn-sm"
              disabled={!canGoStep3}
              onClick={() => { setErr(null); setStep(3); }}
            >
              Next →
            </button>
          </div>
        </>
      )}

      {/* ── Step 3: Data ────────────────────────────────────────────────── */}
      {step === 3 && (
        <>
          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', display:'block', marginBottom:8 }}>
              Datasource
            </label>
            {dsLoading ? <Spinner /> : (
              <select
                className="form-inp"
                value={datasourceId}
                onChange={e => { setDatasourceId(e.target.value); setFeatureConfigId(''); }}
              >
                <option value="">- Select a profiled datasource -</option>
                {datasources.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {d.total_rows != null ? ` · ${d.total_rows.toLocaleString()} rows` : ''}
                    {d.total_columns ? ` · ${d.total_columns} cols` : ''}
                  </option>
                ))}
              </select>
            )}
            {datasources.length === 0 && !dsLoading && (
              <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>
                No profiled datasources found. Upload and profile a dataset first.
              </div>
            )}
          </div>

          <div>
            <label style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', display:'block', marginBottom:4 }}>
              Feature Config
              <span style={{
                marginLeft:6, fontSize:10, padding:'1px 6px', borderRadius:4,
                background:'rgba(232,74,0,0.08)', color:'var(--brand-orange)',
              }}>
                {purposeDef?.configPurpose} only
              </span>
            </label>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:8 }}>
              {!datasourceId
                ? 'Select a datasource first'
                : featureConfigs.length === 0
                  ? `No approved ${purposeDef?.configPurpose} configs for this datasource. Create and approve one in the Data page.`
                  : `${featureConfigs.length} config${featureConfigs.length > 1 ? 's' : ''} available`
              }
            </div>
            {datasourceId && featureConfigs.length > 0 && (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {featureConfigs.map(fc => {
                  const isFork     = !!fc.base_config_id;
                  const isSelected = featureConfigId === fc.id;
                  return (
                    <div key={fc.id}
                      onClick={() => { setFeatureConfigId(fc.id); setShowFork(false); setForkedConfig(null); }}
                      style={{
                        padding:'12px 14px', borderRadius:6, cursor:'pointer',
                        border:`2px solid ${isSelected ? 'var(--brand-orange)' : 'var(--border)'}`,
                        background: isSelected ? 'rgba(232,71,10,.05)' : 'var(--surface-2)',
                        transition:'all .12s',
                      }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        {isFork && (
                          <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3,
                            background:'rgba(139,92,246,.1)', color:'#a78bfa', fontWeight:600 }}>
                            fork v{fc.fork_version}
                          </span>
                        )}
                        <span style={{ fontSize:12, fontWeight:600,
                          color: isSelected ? 'var(--brand-orange)' : 'var(--text-primary)' }}>
                          {fc.config_name}
                        </span>
                        {fc.user_reviewed && (
                          <span style={{ fontSize:9, color:'var(--success)' }}>✓ reviewed</span>
                        )}
                      </div>
                      <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                        {fc.target_column && (
                          <span style={{ fontSize:10, color:'var(--text-muted)' }}>
                            Target: <span style={{ fontFamily:'var(--font-mono)',
                              color:'var(--text-secondary)' }}>{fc.target_column}</span>
                          </span>
                        )}
                        {fc.train_split != null && (
                          <span style={{ fontSize:10, color:'var(--text-muted)' }}>
                            Split: {Math.round(fc.train_split*100)}/{Math.round(fc.val_split*100)}/{Math.round(fc.test_split*100)}
                          </span>
                        )}
                        {isFork && fc.fork_reason && (
                          <span style={{ fontSize:10, color:'var(--text-muted)', fontStyle:'italic' }}>
                            "{fc.fork_reason}"
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Fork & Customise */}
                {featureConfigId && !showFork && (
                  <button className="btn btn-ghost btn-sm"
                    style={{ alignSelf:'flex-start', fontSize:10, marginTop:4 }}
                    onClick={e => { e.stopPropagation(); setShowFork(true); setForkReason(''); }}>
                    ⑂ Fork &amp; Customise
                  </button>
                )}

                {showFork && (
                  <div style={{ padding:'12px 14px', border:'1px solid var(--border)',
                    borderRadius:6, background:'var(--surface-3)', marginTop:4 }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:8 }}>
                      Fork "{featureConfigs.find(f => f.id === featureConfigId)?.config_name}"
                    </div>
                    <div style={{ fontSize:10, color:'var(--text-muted)', marginBottom:10 }}>
                      Creates a copy at project level. Both configs remain available for future runs.
                    </div>
                    <div className="form-grp">
                      <label className="form-lbl">Reason for fork (required)</label>
                      <textarea className="form-inp" rows={2} value={forkReason}
                        onChange={e => setForkReason(e.target.value)}
                        placeholder="What are you changing and why? e.g. Removing low-variance features for churn model" />
                    </div>
                    <div style={{ display:'flex', gap:8, marginTop:8 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setShowFork(false)}>Cancel</button>
                      <button className="btn btn-primary btn-sm"
                        disabled={!forkReason.trim() || forking}
                        onClick={async () => {
                          setForking(true);
                          try {
                            const fc = await apiFetch(
                              `/datasources/${datasourceId}/feature-configs/${featureConfigId}/fork?fork_reason=${encodeURIComponent(forkReason.trim())}`,
                              { method:'POST' }
                            );
                            setForkedConfig(fc);
                            setFeatureConfigId(fc.id);
                            setShowFork(false);
                            // reload feature configs to include the new fork
                            const cfgs = await apiFetch(
                              `/datasources/${datasourceId}/feature-configs?purpose=${purposeDef?.configPurpose || ''}`
                            );
                            setFeatureConfigs((cfgs||[]).filter(c => c.user_reviewed || c.base_config_id));
                          } catch(e) { alert(e.message); }
                          finally { setForking(false); }
                        }}>
                        {forking ? <Spinner size={11}/> : '⑂ Create Fork & Use'}
                      </button>
                    </div>
                  </div>
                )}

                {forkedConfig && (
                  <div style={{ fontSize:10, color:'var(--success)', marginTop:4 }}>
                    ✓ Using fork v{forkedConfig.fork_version}: "{forkedConfig.config_name}"
                  </div>
                )}
              </div>
            )}
          </div>

          {err && <Err msg={err} style={{ marginTop:12 }} />}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:20 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(2)}>← Back</button>
            <button
              className="btn btn-primary btn-sm"
              disabled={!canGoStep4}
              onClick={() => { setErr(null); setStep(4); }}
            >
              Next →
            </button>
          </div>
        </>
      )}

      {/* ── Step 4: Pipeline + Options ──────────────────────────────────── */}
      {step === 4 && isCustom && (
        <>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#64748B',
              textTransform:'uppercase', letterSpacing:'.06em', marginBottom:12 }}>
              Select Custom Script
            </div>
            <p style={{ fontSize:12, color:'#64748B', marginBottom:16 }}>
              Pick the script that will run on the remote server.
              The script must accept <code>--params</code>, <code>--data-path</code>,
              <code>--work-dir</code> and write <code>result.json</code>.
            </p>
            {scriptsLoading ? <Spinner /> : scripts.length === 0 ? (
              <div style={{ padding:'20px', textAlign:'center', color:'#64748B',
                fontSize:13, border:'1px dashed var(--bd)', borderRadius:6 }}>
                No scripts uploaded yet. Go to Plugins → Custom Scripts to upload one.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {scripts.map(s => (
                  <div key={s.script_id}
                    onClick={() => setCustomScriptId(s.script_id)}
                    style={{
                      padding:'12px 16px', borderRadius:6, cursor:'pointer',
                      border: customScriptId === s.script_id
                        ? '2px solid var(--or)' : '1px solid var(--bd)',
                      background: customScriptId === s.script_id
                        ? 'rgba(232,71,10,.08)' : '#E4EAF4',
                    }}>
                    <div style={{ fontWeight:600, fontSize:13 }}>{s.script_name}</div>
                    {s.description && (
                      <div style={{ fontSize:11, color:'#64748B', marginTop:3 }}>
                        {s.description}
                      </div>
                    )}
                    <div style={{ fontSize:10, color:'#64748B', marginTop:4,
                      fontFamily:'var(--mono)' }}>
                      {s.checksum} · {s.created_at?.slice(0,10)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {step === 4 && !isCustom && (
        <>
          {/* Pipeline selector */}
          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', display:'block', marginBottom:8 }}>
              Pipeline
            </label>
            <div style={{ display:'flex', gap:8 }}>
              {[
                ['none',     'Default',         'Platform-managed training'],
                ['template', 'Template',        'Saved pipeline template'],
                ['explicit', 'Custom plugins',  'Select plugins manually'],
              ].map(([val, lbl, hint]) => (
                <div
                  key={val}
                  onClick={() => setPipelineMode(val)}
                  style={{
                    flex:1, padding:'10px 12px', borderRadius:'var(--radius-sm)',
                    cursor:'pointer', textAlign:'center',
                    border:`2px solid ${pipelineMode===val ? 'var(--brand-orange)' : 'var(--border)'}`,
                    background: pipelineMode===val ? 'rgba(232,74,0,0.04)' : 'var(--bg-elevated)',
                  }}
                >
                  <div style={{ fontSize:12, fontWeight:600, color: pipelineMode===val ? 'var(--brand-orange)' : 'var(--text-primary)', marginBottom:3 }}>{lbl}</div>
                  <div style={{ fontSize:10, color:'var(--text-muted)' }}>{hint}</div>
                </div>
              ))}
            </div>
          </div>

          {pipelineMode === 'template' && (
            <div style={{ marginBottom:16 }}>
              <select className="form-inp" value={templateId} onChange={e => setTemplateId(e.target.value)}>
                <option value="">- Select pipeline template -</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.template_name} v{t.version}</option>
                ))}
              </select>
            </div>
          )}

          {pipelineMode === 'explicit' && (
            <div style={{ marginBottom:16 }}>
              {['PREPROCESS', 'TRAIN', 'EVALUATE', 'POSTPROCESS'].map(type => (
                <div key={type} style={{ marginBottom:8 }}>
                  <label style={{ fontSize:10, fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:4 }}>
                    {type}
                  </label>
                  <select
                    className="form-inp"
                    value={explicitPlugins[type] || ''}
                    onChange={e => setExplicitPlugins(p => ({ ...p, [type]: e.target.value || undefined }))}
                  >
                    <option value="">- None (optional) -</option>
                    {(pluginsByType[type] || []).map(p => (
                      <option key={p.id} value={p.id}>{p.name} v{p.version}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {/* NER info banner */}
          {runPurpose === 'NER' && (
            <div style={{
              padding:'10px 14px', borderRadius:'var(--radius-sm)', marginBottom:16,
              background:'rgba(37,99,235,0.06)', border:'1px solid rgba(37,99,235,0.2)',
            }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--info)', marginBottom:4 }}>
                NER requires a TRAIN plugin
              </div>
              <div style={{ fontSize:11, color:'var(--text-muted)' }}>
                Go to <strong>Plugins</strong> and load the NER training template.
                Customise it for your entity types, then select it above as the TRAIN plugin.
              </div>
            </div>
          )}

          {/* Options */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', display:'block', marginBottom:6 }}>
                Run label <span style={{ fontWeight:400, color:'var(--text-muted)' }}>(optional)</span>
              </label>
              <input
                className="form-inp"
                placeholder="e.g. ITSM v2 baseline"
                value={runLabel}
                onChange={e => setRunLabel(e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', display:'block', marginBottom:6 }}>
                Priority
              </label>
              <select className="form-inp" value={priority} onChange={e => setPriority(e.target.value)}>
                <option value="LOW">Low</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High</option>
              </select>
            </div>
          </div>

          {/* Summary */}
          <div style={{
            padding:'10px 14px', borderRadius:'var(--radius-sm)', marginBottom:16,
            background:'var(--bg-elevated)', border:'1px solid var(--border)',
            fontSize:11, color:'var(--text-secondary)', lineHeight:2,
          }}>
            <strong>Purpose:</strong> {purposeDef?.label}
            {' · '}
            <strong>Model:</strong> {selectedModel?.display_name || selectedModel?.model_name || selectedVersionId}
          </div>

          {err && <Err msg={err} />}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(3)}>← Back</button>
            {isCustom ? (
              <button className="btn btn-primary btn-sm"
                disabled={!canGoScript}
                onClick={() => { setErr(null); setStep(5); }}>
                Next →
              </button>
            ) : (
              <button
                className="btn btn-primary btn-sm"
                data-tour="createrun-submit"
                disabled={saving}
                onClick={save}
              >
                {saving ? 'Creating…' : 'Create Run'}
              </button>
            )}
          </div>
        </>
      )}
      {step === 5 && isCustom && (
        <>
          {/* Pipeline selector - same as step 4 for non-CUSTOM */}
          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:11, fontWeight:600, color:'#64748B',
              textTransform:'uppercase', letterSpacing:'.06em', display:'block',
              marginBottom:10 }}>Pipeline (optional)</label>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {[['none','None'],['template','Template'],['explicit','Explicit']].map(([v,l]) => (
                <button key={v} className={`btn btn-sm ${pipelineMode===v?'btn-primary':'btn-ghost'}`}
                  onClick={() => setPipelineMode(v)}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(4)}>← Back</button>
            <button className="btn btn-primary btn-sm" data-tour="createrun-submit" disabled={saving} onClick={save}>
              {saving ? 'Creating…' : 'Create Run'}
            </button>
          </div>
        </>
      )}
        </div>

        {/* ── Live summary sidebar - fills the space next to the narrow
             wizard column and shows every choice made so far, so the
             screen never looks empty and orientation never gets lost. ── */}
        <div style={{ flex:1, minWidth:280, position:'sticky', top:24 }} data-tour="createrun-summary">
          <div style={{
            border:'1px solid var(--border)', borderRadius:'var(--radius-md)',
            background:'var(--bg-surface)', padding:'18px 20px',
          }}>
            <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
              letterSpacing:'.06em', color:'var(--text-muted)', marginBottom:14 }}>
              Run Summary
            </div>
            <SummaryRow label="Purpose" filled={!!runPurpose}
              value={purposeDef ? `${purposeDef.icon} ${purposeDef.label}` : null} />
            <SummaryRow label="Model" filled={!!selectedModel}
              value={selectedModel
                ? (selectedModel.source_type === 'DAPT_ARTIFACT'
                    ? `${selectedModel.dapt_run_label || 'DAPT Run #' + selectedModel.dapt_run_number} (domain-adapted)`
                    : selectedModel.display_name)
                : null} />
            <SummaryRow label="Dataset" filled={!!selectedDs}
              value={selectedDs ? `${selectedDs.name}${selectedDs.total_rows != null ? ' · ' + selectedDs.total_rows.toLocaleString() + ' rows' : ''}` : null} />
            <SummaryRow label="Feature config" filled={!!selectedFc}
              value={selectedFc ? selectedFc.config_name : null} />
            {isCustom && (
              <SummaryRow label="Script" filled={!!selectedScript}
                value={selectedScript ? selectedScript.script_name : null} />
            )}
            <SummaryRow label="Pipeline" filled={pipelineMode !== 'none'}
              value={
                pipelineMode === 'template' ? (selectedTemplate ? `Template: ${selectedTemplate.template_name}` : 'Template') :
                pipelineMode === 'explicit' ? `${explicitCount} plugin${explicitCount === 1 ? '' : 's'} selected` :
                'None'
              } />
            {runLabel && (
              <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid var(--border)',
                fontSize:11, color:'var(--text-muted)' }}>
                Label: <span style={{ color:'var(--text-secondary)' }}>{runLabel}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, filled }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:10 }}>
      <span style={{
        flexShrink:0, width:16, height:16, borderRadius:'50%', marginTop:1,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:9, fontWeight:700,
        background: filled ? 'var(--success)' : 'var(--bg-elevated)',
        color: filled ? '#fff' : 'var(--text-muted)',
        border: filled ? 'none' : '1px solid var(--border)',
      }}>{filled ? '✓' : ''}</span>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase',
          letterSpacing:'.04em' }}>{label}</div>
        <div style={{
          fontSize:12.5, fontWeight: filled ? 600 : 400,
          color: filled ? 'var(--text-primary)' : 'var(--text-muted)',
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>
          {value || 'Not selected yet'}
        </div>
      </div>
    </div>
  );
}

export default CreateRunPage;